import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertUI } from '../components/AlertUI';
import { AlertSystem } from '../services/AlertSystem';
import { Alert, UserInfo } from '../types';

export const useAlertUI = (userInfo: UserInfo | null = null) => {
  const alertSystemRef = useRef<AlertSystem | null>(null);
  const alertUIRef = useRef<AlertUI | null>(null);
  const [alertCount, setAlertCount] = useState(0);
  const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
  const [dismissedAlertIds, setDismissedAlertIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const syncAlertsState = useCallback(() => {
    if (!alertSystemRef.current) return;

    const system = alertSystemRef.current;
    setAllAlerts(system.getAllAlertsForUser());
    setDismissedAlertIds(system.getDismissedAlertIds());
    setAlertCount(system.getAlertsToShow().length);
  }, []);

  const destroyAlertUI = () => {
    if (alertUIRef.current) {
      alertUIRef.current.destroy();
      alertUIRef.current = null;
    }
  };

  const initAlertUI = (alertSystem: AlertSystem) => {
    destroyAlertUI();
    alertUIRef.current = new AlertUI(alertSystem);
    alertUIRef.current.init();
  };

  useEffect(() => {
    let mounted = true;

    const initAlerts = async () => {
      try {
        setIsLoading(true);
        const alertSystem = await AlertSystem.initialize(userInfo);

        if (!mounted) return;

        alertSystemRef.current = alertSystem;
        initAlertUI(alertSystem);
        syncAlertsState();
      } catch (error) {
        console.error('Error initializing alerts:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initAlerts();

    return () => {
      mounted = false;
      destroyAlertUI();
      alertSystemRef.current = null;
    };
  }, [userInfo, syncAlertsState]);

  useEffect(() => {
    const handleAlertDismissed = (event: CustomEvent) => {
      setAlertCount(event.detail.remainingCount);
      syncAlertsState();
    };

    window.addEventListener('alertDismissed', handleAlertDismissed as EventListener);

    return () => {
      window.removeEventListener('alertDismissed', handleAlertDismissed as EventListener);
    };
  }, [syncAlertsState]);

  useEffect(() => {
    const handleEnqueueAlert = (event: Event) => {
      const customEvent = event as CustomEvent<Alert>;
      const incomingAlert = customEvent.detail;

      if (!incomingAlert || !alertSystemRef.current) return;

      alertSystemRef.current.alerts = [incomingAlert, ...alertSystemRef.current.alerts];
      alertUIRef.current?.refreshAlerts();
      syncAlertsState();
    };

    window.addEventListener('enqueueAlert', handleEnqueueAlert);
    return () => window.removeEventListener('enqueueAlert', handleEnqueueAlert);
  }, [syncAlertsState]);

  const dismissAlert = (alertId: string) => {
    if (!alertSystemRef.current) return;
    alertSystemRef.current.dismissAlert(alertId);
    alertUIRef.current?.refreshAlerts();
    syncAlertsState();
    window.dispatchEvent(
      new CustomEvent('alertDismissed', {
        detail: {
          alertId,
          remainingCount: alertSystemRef.current.getAlertsToShow().length,
        },
      }),
    );
  };

  const undismissAlert = (alertId: string) => {
    if (!alertSystemRef.current) return;
    alertSystemRef.current.undismissAlert(alertId);
    alertUIRef.current?.refreshAlerts();
    syncAlertsState();
  };

  const isAlertDismissed = (alertId: string) => {
    return dismissedAlertIds.includes(alertId);
  };

  const refreshAlerts = async () => {
    try {
      setIsLoading(true);
      const alertSystem = await AlertSystem.initialize(userInfo);
      alertSystemRef.current = alertSystem;
      initAlertUI(alertSystem);
      syncAlertsState();
    } catch (error) {
      console.error('Error refreshing alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    alertCount,
    allAlerts,
    dismissedAlertIds,
    isLoading,
    refreshAlerts,
    dismissAlert,
    undismissAlert,
    isAlertDismissed,
  };
};
